import React, { useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'
import useReactRouter from 'use-react-router'
import {
  enableVolumeScheduling,
  listStorageServiceDetails,
} from 'app/plugins/infrastructure/components/common/service-health/action'
import { ErrorMessage } from 'core/components/validatedForm/ErrorMessage'
import Button from 'core/elements/button'
import Modal from 'core/elements/modal'
import Text from 'core/elements/Text'
import useListAction from 'core/hooks/useListAction'
import useUpdateAction from 'core/hooks/useUpdateAction'
import getDataSelector from 'core/utils/getDataSelector'
import { routeManager } from 'core/utils/routes'
import DataKeys from 'k8s/DataKeys'
import { enableHypervisorScheduling, listHypervisors } from '../../actions'

// See DisableMaintenanceDialog.spec.md for FR-001 through FR-011
export default function DisableMaintenanceDialog({ rows: hosts, onClose, onDetailsPage = false }) {
  // FR-011: Only the first selected host is used
  const host = hosts?.[0]
  const { history } = useReactRouter()
  const { reload: reloadHypervisors } = useListAction(listHypervisors)
  const {
    update: enableHypervisor,
    updating: enablingHypervisor,
    error: errorEnablingHypervisor,
    reset: resetEnableHypervisor,
  } = useUpdateAction(enableHypervisorScheduling)

  // Maintenance mode also disabled this host's cinder-volume services, so find and
  // re-enable them (the cache is the raw os-services list, disabled entries included).
  useListAction(listStorageServiceDetails)
  const allStorageServices = useSelector(
    getDataSelector<DataKeys.StorageServiceDetails>(DataKeys.StorageServiceDetails),
  )
  // FR-003: Every disabled cinder-volume service on this host is re-enabled, regardless of
  // disabled_reason (intended: leaving maintenance returns the host to fully schedulable)
  const disabledStorageServices = useMemo(
    () =>
      allStorageServices?.filter(
        (service) =>
          service?.binary === 'cinder-volume' &&
          service?.host?.includes(host?.id) &&
          service?.status === 'disabled',
      ) || [],
    [allStorageServices, host],
  )
  const { update: enableVolume } = useUpdateAction(enableVolumeScheduling)

  // FR-002, FR-005, FR-006, FR-007: Re-enable nova-compute (skipped for storage-only hosts) and
  // all disabled cinder-volume services; storage failures never block the hypervisor path
  const handleDisable = useCallback(async () => {
    const body = {
      host: host?.id,
      binary: 'nova-compute',
    }
    // A storage-only host has no hypervisor to re-enable
    const enableHypervisorPromise = host?.hypervisor?.id
      ? enableHypervisor({ id: host.hypervisor.id, body })
      : Promise.resolve({ success: true })
    // allSettled so a failing storage service doesn't block the rest
    const [{ success }] = await Promise.all([
      enableHypervisorPromise,
      Promise.allSettled(
        disabledStorageServices.map((service) =>
          enableVolume({ body: { host: service.host, binary: 'cinder-volume' } }),
        ),
      ),
    ])
    if (success) {
      reloadHypervisors(true, true)
      if (onDetailsPage) {
        history.push(routeManager.routes.openstack.hosts.path())
      } else {
        handleClose(true)
      }
    }
  }, [host, disabledStorageServices])

  // FR-009: Closing resets the error state and reports no success unless told otherwise
  const handleClose = (success?: boolean) => {
    resetEnableHypervisor()
    onClose(success)
  }

  return (
    <Modal
      panel="dialog"
      title="Disable Maintenance Mode"
      onClose={() => handleClose()}
      open
      footer={
        <>
          <Button variant="secondary" onClick={() => handleClose()} disabled={enablingHypervisor}>
            Cancel
          </Button>
          <Button onClick={handleDisable} loading={enablingHypervisor}>
            Confirm
          </Button>
        </>
      }
    >
      <>
        <Text variant="body2">
          Disable maintenance mode on host <b>{host?.name}</b>. Scheduling will be enabled and
          details on the current maintenance will be lost.
        </Text>
        <Text variant="body2">
          <b>Note:</b> Scheduling will be enabled on all of this host&apos;s services (hypervisor
          and storage). Any service that is currently disabled will be enabled.
        </Text>
        {!!errorEnablingHypervisor && (
          <ErrorMessage>{errorEnablingHypervisor?.message}</ErrorMessage>
        )}
      </>
    </Modal>
  )
}
